/**
 * PATH: packages/email-hub/src/feature/email-reader/navigation.ts
 * FILE_ID: EMAIL-READER-NAV-CONTRACT-001
 * PURPOSE: Pure URL, order, boundary, and terminal-action reader navigation.
 * WHY: Back/Previous/Next must be deterministic across list and full readers.
 * FLOW: ordered visible ids + current id + intent -> URL and target contract.
 */

export const OPEN_EMAIL_SEARCH_PARAM = 'open'

export type ReaderMoveDirection = 'previous' | 'next'
export type ReaderNavigationIntent = 'open-row' | ReaderMoveDirection
export type ReaderHistoryMode = 'push' | 'replace'

export interface ReaderPosition {
  currentId: string | null
  index: number
  total: number
  previousId: string | null
  nextId: string | null
}

export function uniqueReaderOrder(ids: readonly string[]): string[] {
  const seen = new Set<string>()
  const order: string[] = []

  for (const rawId of ids) {
    const id = rawId.trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    order.push(id)
  }

  return order
}

export function resolveReaderPosition(
  ids: readonly string[],
  currentId: string | null | undefined,
): ReaderPosition {
  const order = uniqueReaderOrder(ids)
  const normalizedCurrent = currentId?.trim() || null
  const index = normalizedCurrent ? order.indexOf(normalizedCurrent) : -1

  return {
    currentId: index >= 0 ? order[index] : null,
    index,
    total: order.length,
    previousId: index > 0 ? order[index - 1] : null,
    nextId: index >= 0 && index < order.length - 1 ? order[index + 1] : null,
  }
}

export function resolveReaderMoveTarget(
  ids: readonly string[],
  currentId: string | null | undefined,
  direction: ReaderMoveDirection,
): string | null {
  const position = resolveReaderPosition(ids, currentId)
  return direction === 'previous' ? position.previousId : position.nextId
}

export function resolveTargetAfterRemoval(
  ids: readonly string[],
  removedId: string | null | undefined,
): string | null {
  const position = resolveReaderPosition(ids, removedId)
  return position.nextId ?? position.previousId
}

export function resolveTargetAfterRemovals(
  ids: readonly string[],
  currentId: string | null | undefined,
  removedIds: ReadonlySet<string>,
): string | null {
  const order = uniqueReaderOrder(ids)
  const currentIndex = currentId ? order.indexOf(currentId) : -1
  if (currentIndex < 0 || !currentId || !removedIds.has(currentId)) {
    return currentId?.trim() || null
  }
  for (let index = currentIndex + 1; index < order.length; index += 1) {
    if (!removedIds.has(order[index])) return order[index]
  }
  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    if (!removedIds.has(order[index])) return order[index]
  }
  return null
}

export function readOpenEmailId(params: URLSearchParams): string | null {
  return params.get(OPEN_EMAIL_SEARCH_PARAM)?.trim() || null
}

export function withOpenEmailId(
  params: URLSearchParams,
  emailId: string | null | undefined,
): URLSearchParams {
  const next = new URLSearchParams(params.toString())
  const id = emailId?.trim()
  if (id) next.set(OPEN_EMAIL_SEARCH_PARAM, id)
  else next.delete(OPEN_EMAIL_SEARCH_PARAM)
  return next
}

export function withoutOpenEmailId(params: URLSearchParams): URLSearchParams {
  return withOpenEmailId(params, null)
}

export function historyModeForReaderIntent(
  intent: ReaderNavigationIntent,
): ReaderHistoryMode {
  return intent === 'open-row' ? 'push' : 'replace'
}
